import React from "react";
import { motion } from "framer-motion";
import { Cake, Gift, UserPlus, CheckCircle } from "lucide-react";

const milestoneTypes = [
  {
    icon: <Cake className="h-6 w-6" />,
    title: "Work Anniversaries",
    description: "Automatically celebrate years of service with personalized messages and rewards",
    features: [
      "Year-over-year point scaling",
      "Major milestone tracking (5, 10, 15+ years)",
      "Custom branded messages & logos",
      "Automated scheduling at company time"
    ],
    color: "from-orange-500 to-amber-500"
  },
  {
    icon: <Gift className="h-6 w-6" />,
    title: "Birthdays",
    description: "Make every birthday special with automated celebrations and team recognition",
    features: [
      "Automated birthday posts",
      "Team-wide visibility",
      "Personalized messages with branding",
      "Optional point rewards"
    ],
    color: "from-pink-500 to-rose-500"
  },
  {
    icon: <UserPlus className="h-6 w-6" />,
    title: "Welcome to the Team",
    description: "Automate onboarding with welcome celebrations, swag delivery, and starting points",
    features: [
      "Day-one automated celebrations",
      "Welcome swag delivery coordination",
      "Custom onboarding points allocation",
      "Team introductions with branding"
    ],
    color: "from-blue-500 to-cyan-500"
  }
];

export const MilestoneTypesSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Three Milestones That Matter Most
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Automate celebrations with your branding, personalized messages, and rewards - including swag delivery for new hires
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {milestoneTypes.map((milestone, index) => (
            <motion.div 
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${milestone.color} rounded-full flex items-center justify-center mx-auto mb-6 text-white`}>
                {milestone.icon}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">{milestone.title}</h3>
              <p className="text-gray-600 mb-6 text-center">{milestone.description}</p>
              <div className="space-y-2">
                {milestone.features.map((feature, fIndex) => (
                  <div key={fIndex} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div 
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 max-w-4xl mx-auto border border-blue-100">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Complete Personalization & Branding
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Customize every celebration with your company logo, brand colors, custom messages, and point values. 
              Automate welcome swag delivery for new hires with specific point allocations to kickstart their journey. 
              Set it once and every milestone reflects your unique company culture.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
